import { useState, useEffect } from 'react';
import { Download, Calendar, ChevronDown } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import Avatar from '../components/Avatar';
import StatCard from '../components/Statcard';
import SplitCard from '../components/SplitCard';
import RefreshButton from '../components/RefreshButton';
import api, { getAllPages, logError } from "../services/api";
import { formatKobo, formatNaira } from '../utils/money';
import { formatLagosDate, formatLagosTime } from '../utils/tripLog';

// Commissions and gateway fees are paged on the server; 153 and 168 rows exist.
const LEDGER_PAGE_SIZE = 50;

// One page of a server-paged ledger, reloaded when the page or reloadKey changes.
function useLedgerPage(path, page, reloadKey) {
  const [state, setState] = useState({ rows: [], info: null, loading: true, error: false });
  useEffect(() => {
    let alive = true;
    setState((prev) => ({ ...prev, loading: true }));
    api
      .get(path, { params: { page, limit: LEDGER_PAGE_SIZE } })
      .then((res) => {
        if (alive) setState({ rows: res.data?.data || [], info: res.data?.pagination || null, loading: false, error: false });
      })
      .catch((error) => {
        logError(`Error fetching ${path}:`, error);
        if (alive) setState((prev) => ({ ...prev, loading: false, error: true }));
      });
    return () => {
      alive = false;
    };
  }, [path, page, reloadKey]);
  return state;
}

const PaymentsEarnings = () => {
  const [wallets, setWallets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commissionPage, setCommissionPage] = useState(1);
  const [chargePage, setChargePage] = useState(1);

  // Which parts failed to load, so one bad call doesn't hide the rest.
  const [loadErrors, setLoadErrors] = useState({});
  // Bumped by Retry to re-run the fetch.
  const [reloadKey, setReloadKey] = useState(0);
  const retry = () => setReloadKey((k) => k + 1);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [viewMode, setViewMode] = useState('wallets'); // 'wallets', 'commissions' or 'charges'
  const [walletFilter, setWalletFilter] = useState('all'); // 'all', 'rider', 'customer'

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [walletsRes, summaryRes] = await Promise.allSettled([
          // withBalanceOnly + role are applied in the QUERY, not here.
          // Filtering a page after fetching it can only shrink whatever slice
          // happened to come back: with 149 wallets sorted newest-first and
          // only 26 holding money, nine of them — including a rider with
          // ₦5,350 — fell outside the fetched page and were invisible.
          api.get(
            `/v1/admin/wallets?limit=200&withBalanceOnly=true${
              walletFilter !== 'all' ? `&role=${walletFilter}` : ''
            }`,
          ),
          api.get('/v1/admin/analytics/financial-summary')
        ]);

        // Only replace what loaded; a failed refresh keeps the last data on screen.
        if (walletsRes.status === 'fulfilled') setWallets(walletsRes.value?.data?.data || []);
        if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value?.data || null);
        if (walletsRes.status === 'fulfilled' && summaryRes.status === 'fulfilled') setUpdatedAt(new Date());
        const results = { wallets: walletsRes, summary: summaryRes };
        const failed = {};
        Object.entries(results).forEach(([part, res]) => {
          failed[part] = res.status === 'rejected';
          if (failed[part]) logError(`Error fetching ${part}:`, res.reason);
        });
        setLoadErrors((prev) => ({ ...prev, ...failed }));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [walletFilter, reloadKey]);

  const ledgers = {
    commissions: useLedgerPage('/v1/admin/commissions', commissionPage, reloadKey),
    charges: useLedgerPage('/v1/admin/charges', chargePage, reloadKey),
  };
  const commissions = ledgers.commissions.rows;
  const charges = ledgers.charges.rows;
  const partFailed = (part) => (part === 'wallets' ? loadErrors.wallets : ledgers[part].error);

  const tableLoading = viewMode === 'wallets' ? loading : ledgers[viewMode].loading;
  const ledgerInfo = viewMode === 'wallets' ? null : ledgers[viewMode].info;
  const setLedgerPage = viewMode === 'commissions' ? setCommissionPage : setChargePage;

  const loadError = (what) => (
    <span>
      {`Couldn't load ${what}. `}
      <button type="button" onClick={retry} className="underline font-medium">
        Retry
      </button>
    </span>
  );

  // Format date to "MMM DD, YYYY" format
  const formatDate = (dateString) => {
    return formatLagosDate(dateString, 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format time to "HH:MM AM/PM" format
  const formatTime = (dateString) => {
    return formatLagosTime(dateString, 'en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle export to CSV. The paged ledgers export every row, not just the page on screen.
  const handleExport = async () => {
    let dataToExport = [];
    try {
      if (viewMode === 'wallets') dataToExport = wallets;
      else if (viewMode === 'commissions') dataToExport = await getAllPages('/v1/admin/commissions');
      else if (viewMode === 'charges') dataToExport = await getAllPages('/v1/admin/charges');
    } catch (error) {
      logError('Export failed:', error);
      alert("Couldn't load every row for the export. Please try again.");
      return;
    }

    if (!dataToExport || dataToExport.length === 0) {
      alert('No data to export');
      return;
    }

    let csvContent = "data:text/csv;charset=utf-8,";

    if (viewMode === 'wallets') {
      csvContent += "Name,Transaction ID,Date,Time,Role,Amount,Type,Status\n";
      dataToExport.forEach(item => {
        const row = [
          item.userId?.name || 'System',
          item._id,
          formatDate(item.createdAt),
          formatTime(item.createdAt),
          item.userId?.role || 'System',
          (item.balance / 100).toFixed(2),
          item.type || 'N/A',
          item.status || 'Active'
        ].map(field => `"${field}"`).join(",");
        csvContent += row + "\n";
      });
    } else if (viewMode === 'commissions') {
      csvContent += "Rider Name,Ride ID,Date,Time,Fare,Commission,Status\n";
      dataToExport.forEach(item => {
        const row = [
          item.riderId?.name || 'Unknown',
          item.rideId?._id || 'N/A',
          formatDate(item.createdAt),
          formatTime(item.createdAt),
          (item.fareAmountKobo / 100).toFixed(2),
          (item.commissionAmountKobo / 100).toFixed(2),
          'Completed'
        ].map(field => `"${field}"`).join(",");
        csvContent += row + "\n";
      });
    } else if (viewMode === 'charges') {
      csvContent += "User Name,Reference,Date,Time,Gross Amount,Fee,Provider,Type\n";
      dataToExport.forEach(item => {
        const row = [
          item.userId?.name || 'Unknown',
          item.reference || 'N/A',
          formatDate(item.createdAt),
          formatTime(item.createdAt),
          (item.grossAmountKobo / 100).toFixed(2),
          (item.feeKobo / 100).toFixed(2),
          item.provider || 'N/A',
          item.type || 'N/A'
        ].map(field => `"${field}"`).join(",");
        csvContent += row + "\n";
      });
    }

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${viewMode}_data.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-[#121212]">
      <Sidebar />
      <div className="flex-1 flex flex-col w-full lg:pl-64">
        <main className="flex-1 overflow-y-auto p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Payments & Earnings</h1>
              <p className="text-sm text-gray-500 mt-1">Platform financial overview and ledgers</p>
            </div>
            <div className="flex items-center gap-4">
              <RefreshButton onRefresh={retry} loading={loading} updatedAt={updatedAt} />
              <button
                onClick={handleExport}
                className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-black rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors shadow-sm"
              >
                <Download className="h-4 w-4" />
                Export CSV
              </button>
            </div>
          </div>

          {loadErrors.summary && (
            <div className="mb-4 p-4 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
              {loadError('the financial summary')}
            </div>
          )}

          {/* Financial Summary Cards.
              Six cards total: these four, plus the two combined cards below.
              This page previously carried twenty, several of them the same
              number under different names. */}
          {summary && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <StatCard
                title="Gross Revenue"
                value={formatNaira(summary.revenue?.totalFaresCollected?.ngn)}
                description="Fares collected across all rides. This is passed through to riders — it is not Zooma income."
                className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
              />
              <StatCard
                title="Total Commission"
                value={formatNaira(summary.revenue?.totalCommission?.ngn)}
                description="The portion of fares that Zooma keeps as revenue."
                className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-400"
              />
              {/* Renamed to say what it measures. Promotion spend is shown
                  below for visibility, not subtracted here. */}
              <StatCard
                title="After gateway fees"
                value={formatNaira(summary.profit?.netProfit?.ngn)}
                description="Commission minus Paystack fees. Promotions run on separate budgets and are not charged against this."
                className="bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-400"
              />
              <StatCard
                title="Paystack Fees"
                value={formatNaira(summary.expenditure?.totalPaystackFees?.ngn)}
                description="Total charges paid to Paystack for collections and transfers."
                className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400"
              />
            </div>
          )}

          {/* Two cards, not five sections.
              This page had grown to 20 cards with genuine duplicates — "Money
              in" and "Total collected" were the same number under two names.
              Everything worth knowing about money movement fits in two
              summaries, each with its split shown inline rather than exploded
              across the page. */}
          {summary?.cashflow && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <SplitCard
                title="Total money processed"
                total={formatNaira(summary.cashflow.externalMovement?.totalMoved?.naira)}
                note="Real cash across the payment gateway. Wallet-to-wallet ride charges are not counted here — they move no money."
                rows={[
                  {
                    label: "In",
                    value: formatNaira(summary.cashflow.externalMovement?.moneyIn?.naira),
                    sub: `${summary.cashflow.externalMovement?.moneyInCount || 0} customer fundings`,
                    tone: "text-green-600 dark:text-green-400",
                  },
                  {
                    label: "Out",
                    value: formatNaira(summary.cashflow.externalMovement?.moneyOut?.naira),
                    sub: `${summary.cashflow.externalMovement?.moneyOutCount || 0} rider withdrawals`,
                    tone: "text-orange-600 dark:text-orange-400",
                  },
                ]}
              />
              <SplitCard
                title="Total money in wallets"
                total={formatKobo(summary.cashflow.walletsHeld?.total?.kobo)}
                note="What Zooma is currently holding on behalf of users, plus its own commission account. The rows add up to the total."
                rows={[
                  {
                    label: "Passengers",
                    value: formatKobo(summary.cashflow.walletsHeld?.customers?.kobo),
                    sub: `${summary.cashflow.walletsHeld?.customers?.wallets || 0} wallets`,
                    tone: "text-gray-900 dark:text-white",
                  },
                  {
                    label: "Riders",
                    value: formatKobo(summary.cashflow.walletsHeld?.riders?.kobo),
                    sub: `${summary.cashflow.walletsHeld?.riders?.wallets || 0} wallets — earned, not yet withdrawn`,
                    tone: "text-gray-900 dark:text-white",
                  },
                  {
                    label: "Zooma commission account",
                    value: formatKobo(summary.cashflow.walletsHeld?.platform?.kobo),
                    sub: "Zooma's own money, not users'",
                    tone: "text-gray-900 dark:text-white",
                  },
                  {
                    label: "Deleted accounts",
                    value: formatKobo(summary.cashflow.walletsHeld?.orphaned?.kobo),
                    sub: `${summary.cashflow.walletsHeld?.orphaned?.wallets || 0} wallets whose user was deleted`,
                    tone: "text-gray-900 dark:text-white",
                  },
                ]}
              />
            </div>
          )}

          {/* Tabs, with the wallet role filter beside them.
              The filter used to live in the page header. It worked, but by the
              time you had scrolled down to the table it was off-screen — so the
              list looked unfilterable and mixed riders in with passengers with
              no obvious way to separate them. A control belongs next to the
              thing it controls. */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="flex bg-gray-200 dark:bg-gray-800 p-1 rounded-lg w-fit">
            <button
              onClick={() => setViewMode('wallets')}
              className={`px-6 py-2 text-sm font-medium rounded-md transition-all duration-200 ${viewMode === 'wallets'
                ? 'bg-black text-white shadow-md'
                : 'text-black dark:text-white hover:bg-gray-300 dark:hover:bg-gray-700'
                }`}
            >
              Wallets
            </button>
            <button
              onClick={() => setViewMode('commissions')}
              className={`px-6 py-2 text-sm font-medium rounded-md transition-all duration-200 ${viewMode === 'commissions'
                ? 'bg-black text-white shadow-md'
                : 'text-black dark:text-white hover:bg-gray-300 dark:hover:bg-gray-700'
                }`}
            >
              Commissions
            </button>
            <button
              onClick={() => setViewMode('charges')}
              className={`px-6 py-2 text-sm font-medium rounded-md transition-all duration-200 ${viewMode === 'charges'
                ? 'bg-black text-white shadow-md'
                : 'text-black dark:text-white hover:bg-gray-300 dark:hover:bg-gray-700'
                }`}
            >
              Gateway Fees
            </button>
          </div>

            {viewMode === 'wallets' && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Show
                </span>
                <select
                  value={walletFilter}
                  onChange={(e) => setWalletFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="all">Everyone</option>
                  <option value="customer">Passengers only</option>
                  <option value="rider">Riders only</option>
                </select>
                {!loadErrors.wallets && (
                  <span className="text-sm text-gray-400">
                    {wallets.length} with a balance
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Transactions Table */}
          <div className="bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    {viewMode === 'wallets' && (
                      <>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction ID</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      </>
                    )}
                    {viewMode === 'commissions' && (
                      <>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rider Name</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ride ID</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fare</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Commission</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      </>
                    )}
                    {viewMode === 'charges' && (
                      <>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Name</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gross Amount</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gateway Fee</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-black divide-y divide-gray-200 dark:divide-gray-800">
                  {tableLoading ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                        </div>
                      </td>
                    </tr>
                  ) : partFailed(viewMode) ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-4 text-center text-sm text-red-600">
                        {loadError(viewMode === 'charges' ? 'gateway fees' : viewMode)}
                      </td>
                    </tr>
                  ) : viewMode === 'wallets' ? (
                    wallets.length > 0 ? (
                      wallets.map((wallet) => (
                        <tr key={wallet._id} className="hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                            <div className="flex items-center">
                              <div className="flex-shrink-0">
                                <Avatar
                                  name={wallet.userId?.name || 'System'}
                                  className="h-8 w-8 text-sm"
                                />
                              </div>
                              <div className="ml-3">
                                <div className="font-medium">{wallet.userId?.name || 'System'}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{wallet._id}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(wallet.createdAt)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatTime(wallet.createdAt)}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${!wallet.userId
                              ? 'bg-purple-100 text-purple-800'
                              : wallet.userId.role === 'rider'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-blue-100 text-blue-800'
                              }`}>
                              {!wallet.userId ? 'System' : wallet.userId.role}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                            ₦{(wallet.balance / 100).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              {wallet.status || 'Active'}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">No wallet data available</td>
                      </tr>
                    )
                  ) : viewMode === 'commissions' ? (
                    commissions.length > 0 ? (
                      commissions.map((commission) => (
                        <tr key={commission._id} className="hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                            <div className="flex items-center">
                              <div className="flex-shrink-0">
                                <Avatar
                                  name={commission.riderId?.name || 'Unknown'}
                                  className="h-8 w-8 text-sm"
                                />
                              </div>
                              <div className="ml-3">
                                <div className="font-medium">{commission.riderId?.name || 'Unknown'}</div>
                                <div className="text-xs text-gray-500">{commission.riderId?.phone}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{commission.rideId?._id || 'N/A'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(commission.createdAt)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatTime(commission.createdAt)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                            ₦{(commission.fareAmountKobo / 100).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                            +₦{(commission.commissionAmountKobo / 100).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              Completed
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">No commission data available</td>
                      </tr>
                    )
                  ) : (
                    charges.length > 0 ? (
                      charges.map((charge) => (
                        <tr key={charge._id} className="hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                            <div className="flex items-center">
                              <div className="flex-shrink-0">
                                <Avatar
                                  name={charge.userId?.name || 'Unknown'}
                                  className="h-8 w-8 text-sm"
                                />
                              </div>
                              <div className="ml-3">
                                <div className="font-medium">{charge.userId?.name || 'Unknown'}</div>
                                <div className="text-xs text-gray-500">{charge.userId?.phone}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono text-xs">{charge.reference}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(charge.createdAt)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatTime(charge.createdAt)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                            ₦{(charge.grossAmountKobo / 100).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                            -₦{(charge.feeKobo / 100).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">{charge.provider}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${charge.type === 'collection' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
                              {charge.type}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="px-6 py-4 text-center text-sm text-gray-500">No charge data available</td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
            {ledgerInfo && !partFailed(viewMode) && (
              <nav
                className="flex items-center justify-between border-t border-gray-200 dark:border-gray-800 px-6 py-3 text-sm text-gray-600 dark:text-gray-300"
                aria-label="Ledger pages"
              >
                <span>
                  Page {ledgerInfo.page} of {Math.max(ledgerInfo.totalPages || 1, 1)} ·{' '}
                  {(ledgerInfo.total ?? 0).toLocaleString()} {viewMode === 'charges' ? 'gateway fees' : 'commissions'}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setLedgerPage(ledgerInfo.page - 1)}
                    disabled={!ledgerInfo.hasPrev || tableLoading}
                    className="px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 disabled:opacity-40"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => setLedgerPage(ledgerInfo.page + 1)}
                    disabled={!ledgerInfo.hasNext || tableLoading}
                    className="px-3 py-1.5 rounded-md border border-gray-200 dark:border-gray-700 disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              </nav>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default PaymentsEarnings;
